import React, { useState, useEffect } from "react";
import CharacterForm from "./components/CharacterForm";
import { updateDoc, doc, addDoc, collection, onSnapshot, deleteDoc } from "firebase/firestore";
import { db } from "./firebase";

function App() {
  const [editingCharacter, setEditingCharacter] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [characters, setCharacters] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "list"

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "characters"), (snapshot) => {
      const list = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCharacters(list);
    });

    return () => unsubscribe();
  }, []);

  const handleSubmit = async (character) => {
    try {
      if (character.id) {
        const { id, ...data } = character;
        await updateDoc(doc(db, "characters", id), data);
        alert("Character updated successfully!");
      } else {
        await addDoc(collection(db, "characters"), character);
        alert("Character created successfully!");
      }

      setEditingCharacter(null);
      setShowForm(false);
    } catch (error) {
      console.error("Error submitting character:", error);
      alert("Error saving character. Please try again.");
    }
  };

  const handleDelete = async (id) => {
    if (confirm("Are you sure you want to delete this character?")) {
      try {
        await deleteDoc(doc(db, "characters", id));
        alert("Character deleted successfully!");
      } catch (error) {
        console.error("Error deleting character:", error);
        alert("Error deleting character. Please try again.");
      }
    }
  };

  // Filter characters based on search term
  const filteredCharacters = characters.filter(char =>
    char.characterName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    char.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    char.country.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">AI Character Manager</h1>
              <p className="text-gray-600 mt-1">Manage your AI character collection</p>
            </div>
            
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  setEditingCharacter(null);
                  setShowForm(true);
                }}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Character
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!showForm ? (
          <>
            {/* Search and Filter Bar */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
                <div className="flex-1 max-w-lg">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="Search characters by name, category, or country..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600">View:</span>
                  <div className="flex bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode("grid")}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                        viewMode === "grid" 
                          ? "bg-white text-gray-900 shadow-sm" 
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      Grid
                    </button>
                    <button
                      onClick={() => setViewMode("list")}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                        viewMode === "list" 
                          ? "bg-white text-gray-900 shadow-sm" 
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      List
                    </button>
                  </div>
                  
                  <span className="text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-lg">
                    {filteredCharacters.length} character{filteredCharacters.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>

            {/* Characters Grid/List */}
            {filteredCharacters.length > 0 ? (
              viewMode === "grid" ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredCharacters.map((char) => (
                    <CharacterCard
                      key={char.id}
                      character={char}
                      onEdit={() => {
                        setEditingCharacter(char);
                        setShowForm(true);
                      }}
                      onDelete={() => handleDelete(char.id)}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left py-4 px-6 font-semibold text-gray-900">Character</th>
                          <th className="text-left py-4 px-6 font-semibold text-gray-900">Category</th>
                          <th className="text-left py-4 px-6 font-semibold text-gray-900">Country</th>
                          <th className="text-left py-4 px-6 font-semibold text-gray-900">Status</th>
                          <th className="text-right py-4 px-6 font-semibold text-gray-900">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filteredCharacters.map((char) => (
                          <tr key={char.id} className="hover:bg-gray-50 transition-colors duration-200">
                            <td className="py-4 px-6">
                              <div className="flex items-center gap-3">
                                <img
                                  src={char.imageUrl || "https://via.placeholder.com/60x60"}
                                  alt={char.characterName}
                                  className="w-12 h-12 rounded-lg object-cover"
                                />
                                <div>
                                  <div className="font-medium text-gray-900">{char.characterName}</div>
                                  <div className="text-sm text-gray-500">{char.characterId}</div>
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {char.category}
                              </span>
                            </td>
                            <td className="py-4 px-6 text-gray-600">{char.country || 'N/A'}</td>
                            <td className="py-4 px-6">
                              {char.isTrending ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                  Trending
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  Standard
                                </span>
                              )}
                            </td>
                            <td className="py-4 px-6 text-right space-x-2">
                              <button
                                onClick={() => {
                                  setEditingCharacter(char);
                                  setShowForm(true);
                                }}
                                className="text-blue-600 hover:text-blue-900 font-medium text-sm"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(char.id)}
                                className="text-red-600 hover:text-red-900 font-medium text-sm"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            ) : (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.291-1.1-5.291-2.709M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {searchTerm ? 'No characters found' : 'No characters yet'}
                </h3>
                <p className="text-gray-600 mb-6">
                  {searchTerm 
                    ? `No characters match "${searchTerm}". Try adjusting your search terms.` 
                    : 'Create your first AI character to get started.'
                  }
                </p>
                <button
                  onClick={() => setShowForm(true)}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200"
                >
                  Create Your First Character
                </button>
              </div>
            )}
          </>
        ) : (
          /* Form View */
          <div className="bg-white rounded-xl shadow-sm">
            <div className="border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingCharacter ? 'Edit Character' : 'Create New Character'}
                </h2>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditingCharacter(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6">
              <CharacterForm
                initialData={editingCharacter || {}}
                onSubmit={handleSubmit}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Character Card Component
const CharacterCard = ({ character, onEdit, onDelete }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
      <div className="relative">
        <img
          src={character.imageUrl || "https://via.placeholder.com/400x300"}
          alt={character.characterName}
          className="w-full h-48 object-cover"
        />
        {character.isTrending && (
          <div className="absolute top-3 left-3 bg-yellow-500 text-white text-xs px-2 py-1 rounded-full font-semibold flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            Trending
          </div>
        )}
      </div>
      
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-semibold text-gray-900 text-lg">{character.characterName}</h3>
            <p className="text-sm text-gray-600">{character.characterId}</p>
          </div>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {character.category}
          </span>
        </div>
        
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{character.description}</p>
        
        <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
          <span>📍 {character.country || 'Virtual'}</span>
          <span>💬 {character.prompt ? character.prompt.length : 0} chars</span>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onEdit}
            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200 text-sm"
          >
            Edit
          </button>
          <button
            onClick={onDelete}
            className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-red-700 transition-colors duration-200 text-sm"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default App;
